import Vue from 'vue';
import { Log, UserManager, WebStorageStateStore } from 'oidc-client';
import { tryAuthRedirect } from '../authRedirectGuard';
import { APP_ERROR_KIND, showAppError } from '../appError';

/**
 * Войти не удалось ни тихо, ни редиректом: показываем экран ошибки поверх текущего адреса
 * и отменяем навигацию.
 *
 * Раньше здесь был уход на маршрут /error/401. Он и адрес затирал (после ручного входа
 * пользователь возвращался не туда, куда шёл), и перезагрузкой не лечился — F5 просто
 * перерисовывал заглушку.
 *
 * @param {Function} next Колбэк navigation guard.
 */
function showAuthErrorAt(next) {
    // showAppError идемпотентен: экран мог подняться раньше, внутри redirectToSignIn.
    showAppError(APP_ERROR_KIND.AUTH);
    next(false);
}

// Порт обвязки vue-oidc-client@1.0.0-alpha.4 (vue2, режим SignInType.Window) с исправлениями:
//
// 1. Основной способ входа — тихая авторизация в скрытом iframe. Полный редирект на SSO
//    остался запасным: он уводит со страницы целиком, а пока на стороне SSO жива сессия,
//    уводить незачем. Оригинал всегда делал signinRedirect — и при первом входе, и при
//    каждом протухании токена в фоновой вкладке.
// 2. Тихая попытка одна на всех: навигация и пачка 401 от API приходят почти одновременно,
//    а несколько iframe разом гонялись бы за один и тот же код.
// 3. Продление и редирект откладываются до возвращения пользователя во вкладку: в фоне
//    браузер троттлит таймеры, и редирект случался «за спиной».
// 4. При редиректе сохраняется путь, на который шёл пользователь. В оригинале он терялся
//    из-за бага (state: { current } вместо state: { to }) — после входа всегда «/».
// 5. Полные редиректы на SSO ограничены лимитом (tryAuthRedirect): если авторизация по кругу
//    не помогает, показываем экран ошибки вместо бесконечного перекидывания.
//
// Поддержка popup-сценариев из оригинала не перенесена — приложение их не использует.

export function createOidcAuth(authName, appUrl, oidcConfig) {
    if (!authName) throw new Error('Auth name is required.');
    if (!appUrl) throw new Error('App base url is required.');
    if (!oidcConfig) throw new Error('No config provided to oidc auth.');

    Log.logger = console;
    Log.level = Log.ERROR;

    const nameSlug = slugify(authName);

    const config = {
        response_type: 'id_token',
        scope: 'openid profile',
        automaticSilentRenew: true,
        userStore: new WebStorageStateStore({ store: sessionStorage }),
        post_logout_redirect_uri: appUrl,
        redirect_uri: `${appUrl}auth/signinwin/${nameSlug}`,
        silent_redirect_uri: `${appUrl}auth/signinsilent/${nameSlug}`,
        ...oidcConfig,
    };

    const mgr = new UserManager(config);

    let inited = false;

    const auth = new Vue({
        data() {
            return {
                user: null,
                myRouter: null,
                // Промис идущей тихой авторизации (null, когда она не идёт).
                // http-интерсептор дожидается его, чтобы не слать запросы с пустым токеном.
                renewPromise: null,
            };
        },
        computed: {
            appUrl() {
                return appUrl;
            },
            authName() {
                return authName;
            },
            isAuthenticated() {
                return !!this.user && !this.user.expired;
            },
            accessToken() {
                return !!this.user && !this.user.expired ? this.user.access_token : '';
            },
            events() {
                return mgr.events;
            },
        },
        methods: {
            startup() {
                // Страница, загруженная внутри скрытого iframe тихой авторизации, должна
                // только передать результат родительскому окну — приложение в ней не
                // запускается.
                if (matchesPath(config.silent_redirect_uri)) {
                    mgr.signinSilentCallback();
                    return Promise.resolve(false);
                }

                if (inited) {
                    return Promise.resolve(true);
                }

                return mgr
                    .getUser()
                    .then((user) => {
                        inited = true;
                        if (user && !user.expired) {
                            this.user = user;
                        }

                        // Пользователя в этой вкладке может не быть — это не повод не
                        // запускать приложение: закрытые маршруты разберёт navigation guard,
                        // и вход там начнётся с тихой попытки.
                        return true;
                    })
                    .catch((error) => {
                        Log.warn(`Auth startup err = ${error}`);
                        return false;
                    });
            },
            useRouter(router) {
                this.myRouter = router;
                const signInRouteName = `signinwin-${nameSlug}`;

                router.beforeEach((to, from, next) => {
                    // Защищены все маршруты по умолчанию — кроме страницы, на которую сам
                    // SSO возвращает пользователя: до обмена кода на токен она ещё не
                    // аутентифицирована, а гнать её через ensureSignedIn() значило бы
                    // зациклить редирект.
                    if (to.name === signInRouteName) {
                        next();
                        return;
                    }

                    if (this.isAuthenticated) {
                        next();
                        return;
                    }

                    // Тихий вход проходит в скрытом iframe, поэтому навигацию после него
                    // можно просто продолжить. Редирект же уводит со страницы целиком —
                    // подтверждать переход, который вот-вот отменит выгрузка, незачем.
                    ensureSignedIn(to.fullPath).then(
                        () => (this.isAuthenticated ? next() : next(false)),
                        () => showAuthErrorAt(next),
                    );
                });

                const vroutePath = '/' + getUrlPath(config.redirect_uri).substring((router.options.base || '/').length);

                router.addRoute({
                    path: vroutePath,
                    name: signInRouteName,
                    component: {
                        render: (h) => h('div'),
                        created() {
                            completeRedirectSignIn(router);
                        },
                    },
                });
            },
            /**
             * Вход: сначала тихо, и только если не вышло — редиректом.
             *
             * @param {string} [targetPath] Куда вернуться после входа.
             */
            signIn(targetPath) {
                return ensureSignedIn(targetPath);
            },
            /** Только тихая попытка. Реджектится, когда сессии на стороне SSO нет. */
            signInSilent() {
                return trySilentSignIn();
            },
            /** Только редирект. Реджектится, когда он не состоялся. */
            signInByRedirect(targetPath) {
                return redirectToSignIn(targetPath);
            },
            signOut(args) {
                return mgr.signoutRedirect(args);
            },
            startSilentRenew() {
                mgr.startSilentRenew();
            },
            stopSilentRenew() {
                mgr.stopSilentRenew();
            },
        },
    });

    let redirectCallbackStarted = false;

    /**
     * Возвращение с SSO: меняем код на токен и уходим на путь, сохранённый перед редиректом.
     *
     * Обмен делается ровно один раз за загрузку страницы, хотя компонент коллбэка может
     * создаться повторно: App.vue переключает ветку шаблона, когда догружаются справочники,
     * а маршрут в этот момент ещё прежний. Код одноразовый — второй обмен падал с «No
     * matching state found in storage», и его обработчик уводил на «/», то есть ровно с того
     * адреса, который первый обмен только что восстановил.
     *
     * @param {object} router
     */
    function completeRedirectSignIn(router) {
        if (redirectCallbackStarted) {
            return;
        }
        redirectCallbackStarted = true;

        mgr.signinRedirectCallback()
            .then((data) => {
                // Путь, сохранённый перед уходом на SSO. Без него вход всегда выбрасывал бы
                // пользователя на «/».
                const redirect = data.state ? data.state.to : null;
                router.replace(redirect || '/').catch(() => {});
            })
            .catch((error) => {
                Log.error(`${authName} signin callback error`, error);
                router.replace('/').catch(() => {});
            });
    }

    let pendingSilent = null;

    /**
     * Тихая авторизация: скрытый iframe с prompt=none. Пока на стороне SSO жива сессия,
     * пользователь не видит ни редиректа, ни перезагрузки — это основной путь входа.
     *
     * Попытка одна на всех: параллельные вызовы (навигация плюс пачка 401 от API) делят
     * один промис, иначе несколько iframe гонялись бы за один и тот же код.
     *
     * @returns {Promise<object>} Реджектится, если тихо войти не удалось.
     */
    function trySilentSignIn() {
        if (pendingSilent) {
            return pendingSilent;
        }

        const forget = () => {
            pendingSilent = null;
            auth.renewPromise = null;
        };

        pendingSilent = mgr.signinSilent().then(
            (user) => {
                auth.user = user;
                forget();
                return user;
            },
            (error) => {
                forget();
                throw error;
            },
        );

        // Наружу отдаём заведомо не реджектящуюся обёртку: на renewPromise ждёт перехватчик
        // запросов http.js, и провал тихого входа не должен ронять сам запрос.
        auth.renewPromise = pendingSilent.catch(() => {});

        return pendingSilent;
    }

    /**
     * Полный редирект на SSO — запасной путь, когда тихо войти не вышло.
     *
     * Путь, на который шёл пользователь, уезжает в state: oidc-client вернёт его в
     * signinRedirectCallback, и после входа мы окажемся там же, а не на «/».
     *
     * @param {string} [targetPath] Куда вернуться после входа; по умолчанию — текущий адрес.
     * @returns {Promise<void>} Реджектится, если редирект не состоялся. Экран ошибки к этому
     *          моменту уже поднят.
     */
    function redirectToSignIn(targetPath) {
        if (!tryAuthRedirect()) {
            Log.warn(`${authName} auth redirect limit reached`);
            // Оверлей поверх текущего маршрута вместо перехода на страницу ошибки: адрес
            // сохраняется, и повторный вход возвращает пользователя туда, где он был.
            showAppError(APP_ERROR_KIND.AUTH);

            return Promise.reject(new Error(`${authName} auth redirect limit reached`));
        }

        return mgr.signinRedirect({ state: { to: targetPath || currentPath() } }).catch((error) => {
            // SSO не ответил — вместо застрявшего спиннера показываем экран ошибки.
            showAppError(APP_ERROR_KIND.AUTH);
            throw error;
        });
    }

    /**
     * @param {string} [targetPath] Куда вернуться после входа.
     * @returns {Promise<*>} Реджектится, только если не сработал и редирект.
     */
    function ensureSignedIn(targetPath) {
        return trySilentSignIn().catch(() => redirectToSignIn(targetPath));
    }

    /**
     * Куда вернуть пользователя после входа, если вызывающий код не назвал путь явно.
     *
     * У роутера адрес точнее (fullPath уже без базы и с query), но до его появления берём
     * его из адресной строки — вместе с хэшем, иначе якорь после входа теряется.
     *
     * @returns {string}
     */
    function currentPath() {
        const route = auth.myRouter && auth.myRouter.currentRoute;

        if (route && route.fullPath) {
            return route.fullPath;
        }

        return window.location.pathname + window.location.search + window.location.hash;
    }

    /**
     * Стоит ли пользователь на маршруте, ради которого имеет смысл уходить на SSO.
     * Защищены все маршруты, кроме страницы обратного вызова SSO (см. useRouter()).
     *
     * @returns {boolean}
     */
    function isOnProtectedRoute() {
        const route = auth.myRouter && auth.myRouter.currentRoute;

        return !!route && route.name !== `signinwin-${nameSlug}`;
    }

    let renewPending = false;

    // Протухший токен обновляется в том же порядке, что и первый вход: сначала тихо, редирект
    // только если сессия на SSO умерла. Запуск откладывается до возвращения пользователя во
    // вкладку — в фоне браузер троттлит таймеры, и редирект случался бы «за спиной».
    function renewWhenVisible() {
        if (renewPending) return;
        renewPending = true;

        whenTabVisible(() => {
            trySilentSignIn().then(
                () => {
                    renewPending = false;
                },
                () => {
                    renewPending = false;

                    if (isOnProtectedRoute()) {
                        redirectToSignIn().catch(() => {});
                    }
                },
            );
        });
    }

    mgr.events.addUserLoaded((user) => {
        auth.user = user;
    });

    mgr.events.addUserUnloaded(() => {
        auth.user = null;
    });

    mgr.events.addAccessTokenExpired(() => {
        auth.user = null;
        renewWhenVisible();
    });

    mgr.events.addSilentRenewError(() => {
        if (auth.isAuthenticated) {
            setTimeout(() => trySilentSignIn().catch(() => {}), 5000);
        } else {
            renewWhenVisible();
        }
    });

    mgr.events.addUserSignedOut(() => {
        auth.user = null;
        renewWhenVisible();
    });

    return auth;
}

function whenTabVisible(callback) {
    if (!document.hidden) {
        callback();
        return;
    }

    const onVisible = () => {
        if (document.hidden) return;
        document.removeEventListener('visibilitychange', onVisible);
        callback();
    };
    document.addEventListener('visibilitychange', onVisible);
}

function getUrlPath(url) {
    const a = document.createElement('a');
    a.href = url;
    let path = a.pathname;
    if (path[0] !== '/') path = '/' + path;
    return path;
}

function matchesPath(testUrl) {
    return window.location.pathname.toLocaleLowerCase() === getUrlPath(testUrl).toLocaleLowerCase();
}

function slugify(str) {
    return str
        .replace(/^\s+|\s+$/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-');
}

export default createOidcAuth;
